import type {
  AccessSource,
  CompanyAccessDoc,
  EffectiveAccess,
  RoleAccessDoc,
  UserAccessDoc,
} from "./types";

const NODE_ID_SEPARATOR = "/";
const ACTION_SEPARATOR = ":";

export function nodeAncestors(nodeId: string): string[] {
  const base = nodeId.indexOf(ACTION_SEPARATOR) === -1
    ? nodeId
    : nodeId.slice(0, nodeId.indexOf(ACTION_SEPARATOR));

  if (base !== nodeId) return [base, ...nodeAncestors(base)];

  const parts = nodeId.split(NODE_ID_SEPARATOR);
  const ancestors: string[] = [];

  for (let depth = 1; depth < parts.length; depth += 1) {
    ancestors.push(parts.slice(0, depth).join(NODE_ID_SEPARATOR));
  }

  return ancestors;
}

export function isSelfOrDescendant(nodeId: string, ancestorId: string): boolean {
  return (
    nodeId === ancestorId ||
    nodeId.startsWith(`${ancestorId}${NODE_ID_SEPARATOR}`) ||
    nodeId.startsWith(`${ancestorId}${ACTION_SEPARATOR}`)
  );
}

export function baseNodeId(nodeId: string): string {
  const index = nodeId.indexOf(ACTION_SEPARATOR);
  return index === -1 ? nodeId : nodeId.slice(0, index);
}

export function actionNodeId(nodeId: string, action: string): string {
  return `${nodeId}${ACTION_SEPARATOR}${action}`;
}

function isDeniedBy(denyList: string[], nodeId: string): boolean {
  return denyList.some((denied) => isSelfOrDescendant(nodeId, denied));
}

/** Casillas de un perfil por id (lo mínimo que necesita la expansión). */
export type ProfileAllowById = Record<string, { allow: string[] } | undefined>;

/** Unión de las casillas de los perfiles asignados (los inexistentes se ignoran). */
export function profileBase(
  profileIds: readonly string[] | undefined,
  profilesById: ProfileAllowById,
): Set<string> {
  const base = new Set<string>();

  (profileIds ?? []).forEach((profileId) => {
    (profilesById[profileId]?.allow ?? []).forEach((nodeId) => base.add(nodeId));
  });

  return base;
}

/**
 * Acceso de una capa de rol o compañía con perfiles vivos. Precedencia, de menor
 * a mayor: (1) unión de los perfiles, (2) `deny` propio — quita el nodo, sus
 * descendientes y sus acciones —, (3) `allow` propio. Sin `profile_ids` da lo
 * mismo que antes: el `allow` tal cual. Espejo de `expandAllowLayer` del API.
 */
export function expandAllowLayer(
  layer: { allow?: string[]; profile_ids?: string[]; deny?: string[] } | null,
  profilesById: ProfileAllowById,
): string[] {
  const denyList = layer?.deny ?? [];
  const fromProfiles = [...profileBase(layer?.profile_ids, profilesById)].filter(
    (nodeId) => !isDeniedBy(denyList, nodeId),
  );

  return [...new Set([...fromProfiles, ...(layer?.allow ?? [])])];
}

/**
 * Convierte capas con perfiles asignados en capas planas (la forma de siempre).
 * El API ya las entrega planas en /access/me; esto lo usan los paneles de
 * administración y las pruebas para calcular lo mismo con los perfiles vivos.
 */
export function materializeLayers(
  layers: {
    role: RoleAccessDoc | null;
    user: UserAccessDoc | null;
    company?: CompanyAccessDoc | null;
  },
  profilesById: ProfileAllowById,
): {
  role: RoleAccessDoc | null;
  user: UserAccessDoc | null;
  company: CompanyAccessDoc | null;
} {
  const { role, user, company = null } = layers;

  return {
    role:
      role === null
        ? null
        : { role_id: role.role_id, allow: expandAllowLayer(role, profilesById) },
    company:
      company === null
        ? null
        : {
            company_id: company.company_id,
            allow: expandAllowLayer(company, profilesById),
          },
    user:
      user === null
        ? null
        : {
            user_id: user.user_id,
            mode: user.mode,
            grant: [
              ...new Set([
                ...profileBase(user.profile_ids, profilesById),
                ...user.grant,
              ]),
            ],
            deny: [...user.deny],
          },
  };
}

export interface ResolveAccessInput {
  tenantAllowed: Set<string>;
  company?: CompanyAccessDoc | null;
  role: RoleAccessDoc | null;
  user: UserAccessDoc | null;
}

export function resolveAccess({
  tenantAllowed,
  company = null,
  role,
  user,
}: ResolveAccessInput): EffectiveAccess {
  const allowed = new Set<string>();
  const sourceOf: Record<string, AccessSource> = {};

  const mark = (nodeId: string, source: AccessSource): void => {
    allowed.add(nodeId);
    sourceOf[nodeId] = source;
  };

  const inheritsRole = user === null || user.mode === "inherit";
  const roleAllow = inheritsRole ? (role?.allow ?? []) : [];

  roleAllow.forEach((nodeId) => mark(nodeId, "role"));
  (user?.grant ?? []).forEach((nodeId) => mark(nodeId, "grant"));

  const denyList = user?.deny ?? [];

  denyList.forEach((nodeId) => {
    sourceOf[nodeId] = "deny";
  });

  [...allowed].forEach((nodeId) => {
    if (!isDeniedBy(denyList, nodeId)) return;
    allowed.delete(nodeId);
    sourceOf[nodeId] = "deny";
  });

  [...allowed].forEach((nodeId) => {
    if (tenantAllowed.has(baseNodeId(nodeId))) return;
    allowed.delete(nodeId);
    sourceOf[nodeId] = "unavailable";
  });

  if (company !== null) {
    const companyAllowed = new Set(company.allow);

    [...allowed].forEach((nodeId) => {
      if (companyAllowed.has(nodeId)) return;
      allowed.delete(nodeId);
      sourceOf[nodeId] = "out-of-company";
    });
  }

  [...allowed].forEach((nodeId) => {
    nodeAncestors(nodeId).forEach((ancestorId) => {
      if (allowed.has(ancestorId)) return;
      if (isDeniedBy(denyList, ancestorId)) return;
      if (!tenantAllowed.has(ancestorId)) return;
      mark(ancestorId, "implied");
    });
  });

  return { allowed, sourceOf };
}

export function canAccess(
  effective: EffectiveAccess,
  nodeId: string,
): boolean {
  return effective.allowed.has(nodeId);
}
